/**
 * #628 (increment B) — mid-call "Reconnect to Genesys" affordance.
 *
 * @dylan219 reported ONLINE journeys failing with RBAC 403s despite correct agent
 * profiles: the Genesys↔Webapp online-session link (the pinned interaction id)
 * can DROP mid-call, so the server resolves the state Offline and online-only
 * actions are refused. This banner is the agent-facing recovery: when an
 * online-only action was refused SOLELY because the online link dropped (the BFF
 * marks that on the 403 with `onlineWouldAllow: true` — see
 * `genesys-reconnect-store.ts`), it shows a clear, actionable message and a
 * "Reconnect to Genesys" button — INSTEAD of the bare permission error the agent
 * cannot act on.
 *
 * Non-blocking by design (a slim inline banner, not a modal): the call is still
 * live, so the agent must be able to keep working (unlike the CCF-13 freeze
 * overlay, which deliberately blocks everything). Clicking Reconnect re-pins the
 * mount-frozen interaction id through the EXISTING server-validated
 * `POST /genesys/session-context` route (the #270 participant check), then the
 * app reloads the session; on success the store clears and the banner disappears.
 *
 * SECURITY: this is a RECOVERY path, not a bypass. The button never grants online
 * state itself — it only asks the server to re-validate + re-pin. If the
 * re-validation fails the session stays Offline and the banner remains, so the
 * agent is never optimistically unblocked.
 */

import { useEffect, useState } from 'react';
import {
  getReconnectNeeded,
  subscribeReconnectNeeded,
  type ReconnectNeeded,
} from '../genesys-reconnect-store.js';
import { Button } from './ui/button.js';

export function GenesysReconnectBanner({
  onReconnect,
  reconnecting = false,
}: {
  /** Re-pins the live interaction id server-side (#270-validated) then reloads
   * the session. The parent owns the interaction id + session-reload wiring. */
  onReconnect: () => void;
  /** True while a reconnect attempt is in flight (disables the button). */
  reconnecting?: boolean;
}): JSX.Element | null {
  const [state, setState] = useState<ReconnectNeeded | null>(
    getReconnectNeeded(),
  );
  useEffect(() => subscribeReconnectNeeded(setState), []);

  if (!state) return null;

  return (
    <div
      role="alert"
      data-testid="genesys-reconnect-banner"
      className="flex items-center gap-3 flex-wrap bg-[#FFF4E5] border border-[#FF703E] text-[#1D252C] px-4 py-3 font-['Arial',sans-serif] text-[14px] leading-[1.5]"
    >
      <span className="grow min-w-[220px]">
        <span className="font-bold">Genesys connection dropped.</span> This
        action needs a live call, but the connection to Genesys was lost during
        this call, so it is temporarily unavailable. Reconnect to Genesys to
        continue.
      </span>
      <Button
        type="button"
        onClick={onReconnect}
        disabled={reconnecting}
        aria-label="Reconnect to Genesys"
        data-testid="genesys-reconnect-button"
        className="shrink-0 bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-5 py-2 h-auto font-bold text-[14px]"
      >
        {reconnecting ? 'Reconnecting…' : 'Reconnect to Genesys'}
      </Button>
    </div>
  );
}
